import {
  RefObject,
  useRef,
} from 'react';

const isBrowser = typeof window !== 'undefined';

function getScrollPosition({
  element,
}: {
  element: RefObject<Element> | null
}) {
  if (!isBrowser) return { x: 0, y: 0 };

  if (element) {
    const position = element?.current?.getBoundingClientRect();
    return {
      x: position?.left ?? 0,
      y: position?.top ?? 0,
    };
  }
  return {
    x: -(window.scrollX),
    y: -(window.scrollY),
  };
}

interface PositionsType {
  x: number
  y: number
}

interface CallBackReturnType {
  prevPos: PositionsType,
  currPos: PositionsType,
}

/**
 * @param {func} callback
 * @param {*} ref force rerender on state change only
 * @param {*} element
 * @return {object} scroll position x y
 */
export function useScrollPosition(
  callback: ({
    prevPos,
    currPos,
  }: CallBackReturnType) => void,
  element: RefObject<Element> | null,
): () => void {
  const position = useRef(getScrollPosition({ element }));

  const handleCallBack = () => {
    const currPos = getScrollPosition({ element });
    callback({
      prevPos: position.current,
      currPos,
    });
    position.current = currPos;
  };

  const handleScroll = () => {
    handleCallBack();
  };

  window.addEventListener('scroll', handleScroll);

  return () => window.removeEventListener('scroll', handleScroll);
}
